import { Link } from "@tanstack/react-router";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { TabBar } from "@/app/TabBar";
import { homeworkStatus, useMyHomework, type HomeworkItem } from "@/app/homeworkQueries";
import { useMe } from "@/app/session";
import { count } from "@/lib/plural";
import { StatusChip } from "@/ui/StatusChip";
import { EmptyState, SkeletonRows } from "@/ui/states";
import { NetworkError } from "./route/bits";

/** «Домашки»: сдачи студента по шагам — статус, итерация, вердикт, вход в ревью (S7). */
export function HomeworkPage() {
  const me = useMe();
  const hw = useMyHomework();
  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;
  const items = hw.data ?? [];
  const submitted = items.filter((it) => it.submission);
  const pending = items.filter((it) => !it.submission);
  return (
    <Shell nav user={user} meta="домашки">
      <div className="mx-auto flex w-full max-w-[920px] flex-col gap-6 px-4 pb-20 pt-7 md:px-6">
        <header>
          <div className="mb-3 font-mono text-[11px] uppercase tracking-[.08em] text-accdim">студент · сдачи</div>
          <h1 className="m-0 font-mono text-[28px] font-bold leading-[1.1] tracking-[-.02em] text-ink md:text-[34px]">Домашки</h1>
          <p className="mb-0 mt-3 max-w-[680px] text-[13.5px] leading-[1.55] text-ink2 text-pretty">Зелёная автопроверка — ещё не принятая работа: ментор смотрит процедуру. Здесь каждая сдача со статусом и комментариями к ячейкам.</p>
        </header>
        {hw.status === "pending" && <SkeletonRows rows={4} />}
        {hw.status === "error" && <NetworkError retry={() => void hw.refetch()} />}
        {hw.status === "success" && items.length === 0 && (
          <EmptyState where="студент · домашки" title="Домашек пока нет" body="Первая появится, когда откроется шаг с ноутбуком. Пока — ресурсы и практика шага." primary={{ label: "на мой маршрут", href: "/" }} />
        )}
        {hw.status === "success" && items.length > 0 && (
          <>
            <section data-testid="submitted">
              <h2 className="m-0 mb-2.5 font-mono text-[12px] font-semibold uppercase tracking-[.06em] text-mut">{submitted.length > 0 ? `сдано · ${count(submitted.length, "работа", "работы", "работ")}` : "сдано · пока ничего"}</h2>
              {submitted.length === 0 ? (
                <p className="m-0 text-[13px] text-ink2">Отправка ментору открывается в ноутбуке, когда автопроверка проходит порог.</p>
              ) : (
                <ul className="m-0 flex list-none flex-col gap-3 p-0">
                  {submitted.map((it) => (
                    <SubmittedRow key={it.assignment_id} it={it} />
                  ))}
                </ul>
              )}
            </section>
            {pending.length > 0 && (
              <section data-testid="pending">
                <h2 className="m-0 mb-2.5 font-mono text-[12px] font-semibold uppercase tracking-[.06em] text-mut">ещё не сдано · {count(pending.length, "домашка", "домашки", "домашек")}</h2>
                <ul className="m-0 flex list-none flex-col gap-2 p-0">
                  {pending.map((it) => (
                    <li key={it.assignment_id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed border-bd px-4 py-3" data-testid="pending-row">
                      <span className="flex flex-col gap-0.5">
                        <span className="font-mono text-[11px] text-mut">{it.track.toLowerCase()}</span>
                        <span className="font-mono text-[13.5px] text-ink">{it.step_title}</span>
                      </span>
                      <Link to="/notebook/$assignmentId" params={{ assignmentId: it.assignment_id }} className="font-mono text-[12px] text-acc no-underline hover:underline">
                        {it.versions > 0 ? "продолжить ноутбук →" : "открыть ноутбук →"}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
      <TabBar />
    </Shell>
  );
}

const fmt = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export function verdictLine(it: HomeworkItem): string {
  const s = it.submission!;
  const when = fmt.format(new Date(s.submitted_at));
  switch (s.status) {
    case "on_review":
      return `отправлено ${when} · ждёт ментора`;
    case "changes_requested":
      return `${s.verdict_at ? `возвращено ${fmt.format(new Date(s.verdict_at))}` : "возвращено"}${s.reviewer ? ` · ${s.reviewer.name}` : ""} — правки по комментариям к ячейкам`;
    case "accepted":
      return `${s.verdict_at ? `принято ${fmt.format(new Date(s.verdict_at))}` : "принято"}${s.reviewer ? ` · ${s.reviewer.name}` : ""}`;
  }
}

function SubmittedRow({ it }: { it: HomeworkItem }) {
  const s = it.submission!;
  return (
    <li className="grid grid-cols-1 gap-3 rounded-md border border-bd bg-card p-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-center" data-testid="homework-row">
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="font-mono text-[11px] text-mut">{it.track.toLowerCase()}</span>
          <StatusChip status={homeworkStatus(it)} compact />
          <span className="font-mono text-[11px] text-mut">v{s.iteration}</span>
        </div>
        <Link to="/step/$id" params={{ id: it.step_id }} className="font-mono text-[15px] font-semibold text-ink no-underline hover:underline">
          {it.step_title}
        </Link>
        <div className="font-mono text-[11.5px] text-ink2" data-testid="verdict-line">
          {verdictLine(it)}
        </div>
        {s.summary && <p className="m-0 text-[13px] leading-[1.5] text-ink2 text-pretty">{s.summary}</p>}
      </div>
      <Link to="/notebook/$assignmentId/review" params={{ assignmentId: it.assignment_id }} className="inline-flex items-center justify-center rounded-sm border border-bd2 bg-accsoft px-3.5 py-2 font-mono text-[12.5px] text-acc no-underline hover:border-acc">
        {s.status === "changes_requested" ? "смотреть правки" : "открыть ревью"}
      </Link>
    </li>
  );
}
